import { useEffect, useState, useCallback } from "react";
import { Loader2, Star, Trash2, Eye, EyeOff, MessageSquare, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { OVERLINE, H1, useAdmin } from "@/components/admin/ui";

const TABS = [["", "All"], ["published", "Published"], ["hidden", "Hidden"]];
const Stars = ({ n }) => <span className="inline-flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={13} className={i <= n ? "fill-saddle text-[#8B5E34]" : "text-[#C7C2B8]"} />)}</span>;

export default function AdminCommerceReviews() {
  const { admin } = useAdmin();
  const canEdit = admin?.permissions?.includes("marketplace");
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("");

  const load = useCallback(() => { api.admReviews({ status: tab || undefined }).then(setRows).catch(() => setRows([])); }, [tab]);
  useEffect(() => { load(); }, [load]);

  const moderate = async (r, action) => { try { await api.admReviewModerate(r.id, action); load(); } catch { toast.error("Failed"); } };
  const del = async (r) => { if (!window.confirm("Delete this review permanently?")) return; try { await api.admReviewDelete(r.id); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="p-6 md:p-8" data-testid="admin-commerce-reviews">
      <div><span className={OVERLINE}>Commerce</span><h1 className={`${H1} mt-1`}>Product reviews</h1></div>
      <p className="text-sm text-[#5C6B61] mt-2">Moderate customer reviews. Hidden reviews stay in the database but never show on the storefront.</p>

      <div className="flex gap-1.5 mt-5 mb-4">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 text-xs border ${tab === k ? "bg-[#1F2A22] text-white border-[#1F2A22]" : "border-[#E5E2DC] text-[#5C6B61]"}`} data-testid={`reviews-tab-${k || "all"}`}>{l}</button>)}
      </div>

      {rows === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#FF6A00]" /></div>
      ) : rows.length === 0 ? (
        <div className="py-12 text-center text-[#5C6B61]"><MessageSquare size={34} className="mx-auto text-[#C7C2B8]" /><p className="mt-3">No reviews yet.</p></div>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {rows.map((r) => (
            <div key={r.id} className="border border-[#E5E2DC] bg-white p-4" data-testid={`review-row-${r.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Stars n={r.rating} />
                    {r.verified_buyer && <span className="flex items-center gap-1 text-[11px] text-[#2E7D46] font-medium"><BadgeCheck size={12} /> Verified</span>}
                    <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 ${r.status === "hidden" ? "bg-[#E5E2DC] text-[#5C6B61]" : "bg-[#2E7D46]/15 text-[#2E7D46]"}`}>{r.status}</span>
                  </div>
                  {r.title && <p className="font-medium text-[#1F2A22] mt-1.5 text-sm">{r.title}</p>}
                  {r.body && <p className="text-[#5C6B61] text-sm mt-1">{r.body}</p>}
                  <p className="text-[11px] text-[#8A8478] mt-2">{r.author_name} · on <b>{r.product_title || "product"}</b></p>
                </div>
                {canEdit && (
                  <div className="flex flex-col gap-2 shrink-0">
                    {r.status === "published"
                      ? <button onClick={() => moderate(r, "hide")} className="text-xs border border-[#E5E2DC] text-[#5C6B61] px-2 py-1 inline-flex items-center gap-1" data-testid={`review-hide-${r.id}`}><EyeOff size={12} /> Hide</button>
                      : <button onClick={() => moderate(r, "publish")} className="text-xs border border-[#2E7D46] text-[#2E7D46] px-2 py-1 inline-flex items-center gap-1" data-testid={`review-publish-${r.id}`}><Eye size={12} /> Publish</button>}
                    <button onClick={() => del(r)} className="text-xs border border-[#B04A3A] text-[#B04A3A] px-2 py-1 inline-flex items-center gap-1" data-testid={`review-delete-${r.id}`}><Trash2 size={12} /> Delete</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
